import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft, Trophy, Lock } from 'lucide-react';
import api from '../utils/api';
import { SPORTS_CONFIG } from '../config/sports';

export default function Achievements() {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const [achievements, setAchievements] = useState(null);
  const [athlete, setAthlete] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSport, setSelectedSport] = useState(null);

  useEffect(() => {
    fetchData();
  }, [athleteId]);

  const fetchData = async () => {
    try {
      const [achievementRes, athleteRes] = await Promise.all([
        api.get(`/achievements/${athleteId}`),
        api.get(`/athletes/${athleteId}`)
      ]);
      setAchievements(achievementRes.data);
      setAthlete(athleteRes.data);
      if (athleteRes.data.sports?.length > 0) {
        setSelectedSport(athleteRes.data.sports[0]);
      }
    } catch (error) {
      toast.error('Failed to load achievements');
      navigate(`/athlete/${athleteId}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || !achievements || !athlete) {
    return (
      <div className="min-h-screen bg-[#000] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00E5E5] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const selectedAchievements = selectedSport ? achievements.sports[selectedSport] || [] : [];
  const unlockedCount = selectedAchievements.filter(a => a.unlocked).length;
  const totalCount = selectedAchievements.length;

  return (
    <div className="min-h-screen bg-[#000] pb-8">
      {/* Header */}
      <div className="p-4 flex items-center">
        <button
          data-testid="back-btn"
          onClick={() => navigate(`/athlete/${athleteId}`)}
          className="flex items-center gap-1 text-white"
        >
          <ChevronLeft className="w-6 h-6" />
          <span>Back</span>
        </button>
      </div>

      <div className="px-4">
        {/* Title */}
        <div className="text-center mb-6">
          <Trophy className="w-12 h-12 text-[#00E5E5] mx-auto mb-2" />
          <h1 className="text-2xl font-bold text-white">{athlete.name}'s Achievements</h1>
          <p className="text-gray-400">{achievements.total_games} total games played</p>
        </div>

        {/* Sport Filter */}
        {athlete.sports?.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-4 mb-4">
            {athlete.sports.map(sport => {
              const config = SPORTS_CONFIG[sport];
              const gamesCount = achievements.total_games_by_sport?.[sport] || 0;
              return (
                <button
                  key={sport}
                  data-testid={`achievement-sport-${sport}`}
                  onClick={() => setSelectedSport(sport)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                    selectedSport === sport
                      ? 'bg-[#00E5E5] text-black'
                      : 'bg-[#1A1A1A] text-gray-400'
                  }`}
                >
                  <span>{config?.emoji}</span>
                  <span className="font-semibold">{gamesCount}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Progress */}
        <div className="bg-[#1A1A1A] rounded-2xl p-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-400">Progress</span>
            <span className="text-[#00E5E5] font-bold">{unlockedCount}/{totalCount}</span>
          </div>
          <div className="h-2 bg-[#333] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#00E5E5] rounded-full transition-all"
              style={{ width: `${totalCount > 0 ? (unlockedCount / totalCount) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* Achievements Grid */}
        <div className="space-y-3">
          {selectedAchievements.map((achievement) => (
            <div
              key={achievement.id}
              data-testid={`achievement-${achievement.id}`}
              className={`rounded-2xl p-4 flex items-center gap-4 ${
                achievement.unlocked ? 'bg-gradient-to-r from-[#00E5E5]/20 to-transparent' : 'bg-[#1A1A1A]'
              }`}
            >
              <div
                className={`w-14 h-14 rounded-full flex items-center justify-center ${
                  achievement.unlocked ? 'bg-[#00E5E5]' : 'bg-[#333]'
                }`}
              >
                {achievement.unlocked ? (
                  <span className="text-2xl">{achievement.emoji}</span>
                ) : (
                  <Lock className="w-6 h-6 text-gray-500" />
                )}
              </div>
              <div className="flex-1">
                <h3 className={`font-semibold ${achievement.unlocked ? 'text-white' : 'text-gray-500'}`}>
                  {achievement.name}
                </h3>
                <p className="text-sm text-gray-400">{achievement.description}</p>
                {achievement.value !== null && achievement.threshold && (
                  <p className="text-xs text-gray-500 mt-1">
                    Best: {achievement.value}/{achievement.threshold}
                  </p>
                )}
              </div>
              {achievement.unlocked && (
                <span className="text-[#00E5E5] text-2xl">✓</span>
              )}
            </div>
          ))}
        </div>

        {selectedAchievements.length === 0 && (
          <div className="text-center py-10">
            <p className="text-gray-400">No achievements for this sport yet</p>
          </div>
        )}

        {/* Career Stats */}
        <div className="mt-8">
          <h2 className="text-lg font-bold text-white mb-3">Career Stats</h2>
          <div className="bg-[#1A1A1A] rounded-2xl p-4">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-3xl font-bold text-[#00E5E5]">{achievements.total_games}</p>
                <p className="text-sm text-gray-400">Total Games</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#FF9500]">{achievements.career_stats?.total_points || 0}</p>
                <p className="text-sm text-gray-400">Total Points</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
